import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Divider,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Print,
  Download,
  DragIndicator,
  Reorder
} from '@mui/icons-material';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import LanguageIcon from '@mui/icons-material/Language';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const Resume12 = ({ 
  color = '#36454F',
  font = "Arial, sans-serif",
  fontSize = "14px",
  fontStyle = "normal",
  headingSize = "24px",
  sectionSpacing = 20,
  paragraphSpacing = 10,
  lineSpacing = 20,
  topBottomMargin = 40,
  sideMargins = 40,
  paragraphIndent = 0,
  lineWeight = 1,
  pageSize = "A4",
  theme,
  formData = {},
  photo = "",
  workExperiences = [],
  exporting = false,
  enableDragDrop = true
}) => {
  const primaryColor = theme?.primary || color;
  const darkColor = theme?.dark || getDarkColor(primaryColor);
  
  const [educationEntries, setEducationEntries] = useState([]);
  const [savedSkills, setSavedSkills] = useState([]);
  const [savedSummary, setSavedSummary] = useState("");
  const [savedAdditionalInfo, setSavedAdditionalInfo] = useState([]);
  const [savedLanguages, setSavedLanguages] = useState([]);
  const [savedAccomplishments, setSavedAccomplishments] = useState([]);
  const [savedCertifications, setSavedCertifications] = useState([]);
  const [savedReferences, setSavedReferences] = useState([]);
  const [softwareSkills, setSoftwareSkills] = useState([]);
  const [savedVolunteering, setSavedVolunteering] = useState([]);
  const [savedInterests, setSavedInterests] = useState([]);
  const [savedWebsites, setSavedWebsites] = useState([]);
  const [customSections, setCustomSections] = useState({});

  const [currentPage, setCurrentPage] = useState(0);
  const [pages, setPages] = useState([]);
  const resumeRef = useRef(null);

  const [sectionOrder, setSectionOrder] = useState([]);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [arrangeDialogOpen, setArrangeDialogOpen] = useState(false);

  const pageSizeMap = {
    A4: { width: "210mm", height: "297mm" },
    Letter: { width: "216mm", height: "279mm" },
    Legal: { width: "216mm", height: "356mm" },
    A3: { width: "297mm", height: "420mm" },
    Executive: { width: "184mm", height: "267mm" },
  };

  const page = pageSizeMap[pageSize] || pageSizeMap.A4;

  const defaultData = {
    firstName: "John",
    lastName: "Doe",
    currentPosition: "Senior Graphic Designer",
    phone: "+1 (555) 123-4567",
    email: "johndoe@email.com",
    city: "New York, NY",
    linkedIn: "",
    profile: "A creative and results-driven graphic designer with over 7 years of experience in visual branding and marketing. Passionate about creating designs that solve business problems and delight customers. Seeking to leverage skills in design leadership to contribute to a dynamic creative team."
  };

  const data = { ...defaultData, ...formData };
  
  const defaultSectionOrder = [
    'about',
    'experience',
    'education',
    'skills',
    'languages',
    'certifications',
    'accomplishments',
    'additionalInfo',
    'volunteering',
    'interests',
    'websites',
    'references'
  ];

  const textStyle = {
    color: primaryColor,
    fontFamily: font,
    fontSize: fontSize,
    fontStyle: fontStyle,
    lineHeight: `${lineSpacing}px`
  };

  // Heading style — uses headingSize, NOT fontSize
  const headingStyle = {
    fontSize: headingSize,
    fontWeight: "bold",
    color: primaryColor,
    fontFamily: font,
    fontStyle: fontStyle,
  };

  function getDarkColor(hex) {
    let c = hex.replace('#', '');
    if (c.length === 8) c = c.slice(0, 6);
    let r = Math.max(0, parseInt(c.substring(0,2),16) - 40);
    let g = Math.max(0, parseInt(c.substring(2,4),16) - 40);
    let b = Math.max(0, parseInt(c.substring(4,6),16) - 40);
    return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`;
  }

  useEffect(() => {
    const storedEntries = localStorage.getItem("educationEntries");
    if (storedEntries) setEducationEntries(JSON.parse(storedEntries));
    
    const storedSkills = localStorage.getItem("skills");
    if (storedSkills) setSavedSkills(JSON.parse(storedSkills));
    
    const storedSummaries = JSON.parse(localStorage.getItem("summaries")) || [];
    if (storedSummaries.length > 0) setSavedSummary(storedSummaries[storedSummaries.length - 1]);
    
    const storedAdditionalInfo = JSON.parse(localStorage.getItem("additionalInfo")) || [];
    setSavedAdditionalInfo(storedAdditionalInfo);
    
    const storedLanguages = JSON.parse(localStorage.getItem("languagesList")) || [];
    setSavedLanguages(storedLanguages);
    
    const storedAccomplishments = JSON.parse(localStorage.getItem("accomplishmentsList")) || [];
    setSavedAccomplishments(storedAccomplishments);
    
    const storedCertifications = JSON.parse(localStorage.getItem("certificationsList")) || [];
    setSavedCertifications(storedCertifications);
    
    const storedReferences = JSON.parse(localStorage.getItem("referencesList")) || [];
    setSavedReferences(storedReferences);
    
    const storedSoftwareSkills = JSON.parse(localStorage.getItem("softwareSkills")) || [];
    setSoftwareSkills(storedSoftwareSkills);
    
    const storedVolunteering = JSON.parse(localStorage.getItem("volunteering")) || [];
    setSavedVolunteering(storedVolunteering);
    
    const storedInterests = JSON.parse(localStorage.getItem("interests")) || [];
    setSavedInterests(storedInterests);
    
    const storedWebsites = JSON.parse(localStorage.getItem("websites")) || [];
    setSavedWebsites(storedWebsites);
    
    const userId = localStorage.getItem("userId");
    console.log("👤 Current userId:", userId);
    
    let sectionsData = {};

    if (userId) {
      const currentCustomSections = JSON.parse(localStorage.getItem(`current_custom_sections_${userId}`)) || [];
      console.log("🔍 Current custom sections to display:", currentCustomSections);
      
      if (currentCustomSections.length > 0) {
        currentCustomSections.forEach(sectionName => {
          const userCustomKey = `custom_${userId}_${sectionName}`;
          try {
            const customSectionData = localStorage.getItem(userCustomKey);
            if (customSectionData) {
              const parsedData = JSON.parse(customSectionData);
              const items = parsedData.items || [];
              const validItems = items.filter(item => item && item.trim() !== '');
              if (validItems.length > 0) {
                sectionsData[sectionName] = validItems;
                console.log(`✅ Loaded current custom section: ${sectionName} with ${validItems.length} items`);
              }
            }
          } catch (error) {
            console.error(`❌ Error loading current custom section ${sectionName}:`, error);
          }
        });
      }
    }

    setCustomSections(sectionsData);

    const savedSectionOrder = localStorage.getItem("resume12SectionOrder");
    if (savedSectionOrder) {
      const parsedOrder = JSON.parse(savedSectionOrder);
      const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      const filteredOrder = parsedOrder.filter(section => !section.startsWith('custom_'));
      const allSections = [...filteredOrder, ...currentCustomSectionIds];
      setSectionOrder(allSections);
    } else {
      const currentCustomSectionIds = Object.keys(sectionsData).map(name => `custom_${name}`);
      const updatedSectionOrder = [...defaultSectionOrder, ...currentCustomSectionIds];
      setSectionOrder(updatedSectionOrder);
    }
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (userId && Object.keys(customSections).length > 0) {
      const currentOrder = [...sectionOrder];
      const currentCustomSectionIds = Object.keys(customSections).map(name => `custom_${name}`);
      const filteredOrder = currentOrder.filter(section => !section.startsWith('custom_'));
      const newOrder = [...filteredOrder, ...currentCustomSectionIds];
      if (JSON.stringify(newOrder) !== JSON.stringify(currentOrder)) {
        setSectionOrder(newOrder);
        localStorage.setItem("resume12SectionOrder", JSON.stringify(newOrder));
      }
    } else if (userId && Object.keys(customSections).length === 0) {
      const currentOrder = [...sectionOrder];
      const filteredOrder = currentOrder.filter(section => !section.startsWith('custom_'));
      if (filteredOrder.length !== currentOrder.length) {
        setSectionOrder(filteredOrder);
        localStorage.setItem("resume12SectionOrder", JSON.stringify(filteredOrder));
      }
    }
  }, [customSections]);

  useEffect(() => {
    if (sectionOrder.length > 0) {
      localStorage.setItem("resume12SectionOrder", JSON.stringify(sectionOrder));
    }
  }, [sectionOrder]);

  const formatDate = (month, year) => {
    if (!month || !year) return "";
    return `${month} ${year}`;
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ index, sectionId: sectionOrder[index] }));
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
  };

  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData('text/plain'));
    const sourceIndex = data.index;
    if (sourceIndex !== targetIndex) {
      const newSectionOrder = [...sectionOrder];
      const [movedSection] = newSectionOrder.splice(sourceIndex, 1);
      newSectionOrder.splice(targetIndex, 0, movedSection);
      setSectionOrder(newSectionOrder);
    }
    setDragOverIndex(null);
    e.currentTarget.style.opacity = '1';
  };

  const handleDragEnd = (e) => {
    setDragOverIndex(null);
    e.currentTarget.style.opacity = '1';
  };

  const resetSectionOrder = () => setSectionOrder(defaultSectionOrder);
  const handleArrangeSectionsClick = () => setArrangeDialogOpen(true);
  const handleArrangeDialogClose = () => setArrangeDialogOpen(false);

  const getSectionTitle = (sectionId) => {
    const titles = {
      'about': 'About Me', 'skills': 'Skills', 'education': 'Education',
      'experience': 'Experience', 'languages': 'Languages', 'certifications': 'Certifications',
      'accomplishments': 'Accomplishments', 'softwareSkills': 'Software Skills',
      'volunteering': 'Volunteering', 'interests': 'Interests',
      'websites': 'Websites & Portfolios', 'references': 'References',
      'additionalInfo': 'Additional Information'
    };
    if (sectionId.startsWith('custom_')) {
      const customName = sectionId.replace('custom_', '').replace(/_/g, ' ');
      return customName.charAt(0).toUpperCase() + customName.slice(1);
    }
    return titles[sectionId] || sectionId;
  };

  // ─── SECTION HEADING COMPONENT ───────────────────────────────────────────────
  // Renders a heading with headingSize applied as an explicit inline style.
  // This survives outerHTML capture + dangerouslySetInnerHTML re-injection
  // because it is a real inline style (not a MUI sx class).
  const SectionHeading = ({ children }) => (
    <div
      style={{
        fontSize: headingSize,          // ← headingSize, not fontSize
        fontWeight: "bold",
        color: primaryColor,
        fontFamily: font,
        fontStyle: fontStyle,
        marginBottom: `${paragraphSpacing}px`,
        borderBottom: `${lineWeight * 2}px solid ${primaryColor}`,
        paddingBottom: "4px",
        display: "inline-block",
      }}
    >
      {children}
    </div>
  );

  const renderDragPanel = () => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
        <DragIndicator sx={{ mr: 1 }} />
        Resume Sections
        <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
          ({sectionOrder.length} sections)
        </Typography>
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Drag and drop to reorder sections.
      </Typography>
      <Box sx={{ maxHeight: 400, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
        {sectionOrder.length > 0 ? (
          sectionOrder.map((sectionId, index) => (
            <Paper
              key={sectionId}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              sx={{
                p: 2, mb: 1, display: 'flex', alignItems: 'center',
                backgroundColor: dragOverIndex === index ? '#f0f0f0' : 'white',
                boxShadow: dragOverIndex === index ? 2 : 1,
                cursor: 'grab', '&:active': { cursor: 'grabbing' },
                transition: 'all 0.2s ease',
              }}
            >
              <DragIndicator color="action" />
              <Typography sx={{ ml: 1, flexGrow: 1 }}>{getSectionTitle(sectionId)}</Typography>
              <Typography variant="body2" color="textSecondary">{index + 1}</Typography>
            </Paper>
          ))
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
            No sections available.
          </Typography>
        )}
      </Box>
      <Button onClick={resetSectionOrder} variant="outlined" size="small" sx={{ mt: 1 }}>
        Reset Section Order
      </Button>
    </Box>
  );

  const renderSectionContent = (sectionId) => {
    switch (sectionId) {
      case 'about':
        return savedSummary ? (
          <Box mb={`${sectionSpacing}px`} className="resume-section" id="about-section" sx={{ fontFamily: font, ...textStyle }}>
            <SectionHeading>About Me</SectionHeading>
            <Typography sx={{ mt: `${paragraphSpacing / 4}px`, textIndent: `${paragraphIndent}px`, fontFamily: font, color: primaryColor, lineHeight: `${lineSpacing}px` }}>
              {savedSummary}
            </Typography>
          </Box>
        ) : null;

      case 'experience':
        return (workExperiences && workExperiences.length > 0) ? (
          <Box mb={`${sectionSpacing}px`} className="resume-section" id="experience-section" sx={{ fontFamily: font, ...textStyle }}>
            <SectionHeading>Experience</SectionHeading>
            {workExperiences.map((work, i) => (
              <Box key={i} sx={{ mb: `${paragraphSpacing}px`, fontFamily: font }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6" fontWeight="bold" color={primaryColor} sx={textStyle}>{work.jobTitle}</Typography>
                  <Typography variant="body2" color={primaryColor} sx={textStyle}>
                    {formatDate(work.startMonth, work.startYear)} - {work.current ? "Present" : formatDate(work.endMonth, work.endYear)}
                  </Typography>
                </Box>
                <Typography variant="subtitle2" color={primaryColor} sx={{ mb: 1, ...textStyle }}>
                  {work.employer} | {work.city}
                </Typography>
                {work.description && (
                  <Box color={primaryColor}
                    sx={{
                      "& strong": { fontWeight: "bold", fontFamily: font, fontSize: fontSize, fontStyle: fontStyle },
                      "& em": { fontStyle: "italic", fontFamily: font, fontSize: fontSize },
                      "& u": { textDecoration: "underline", fontFamily: font, fontSize: fontSize, fontStyle: fontStyle },
                      "& *": { fontFamily: font, fontSize: fontSize, fontStyle: fontStyle, lineHeight: `${lineSpacing}px` },
                      ...textStyle
                    }}
                    dangerouslySetInnerHTML={{
                      __html: work.description
                        .replace(/\*\*(.*?)\*\*/g, `<strong style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle};">$1</strong>`)
                        .replace(/\*(.*?)\*/g, `<em style="font-family: ${font}; font-size: ${fontSize};">$1</em>`)
                        .replace(/<u>(.*?)<\/u>/g, `<u style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle};">$1</u>`)
                        .replace(/\n/g, "<br/>"),
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        ) : null;

      case 'education':
        return (educationEntries.length > 0) ? (
          <Box mb={`${sectionSpacing}px`} className="resume-section" id="education-section" sx={{ fontFamily: font, ...textStyle }}>
            <SectionHeading>Education</SectionHeading>
            {educationEntries.map((edu) => (
              <Box key={edu.id} sx={{ mb: `${paragraphSpacing}px`, fontFamily: font }}>
                <Typography variant="h6" fontWeight="bold" color={primaryColor} sx={textStyle}>
                  {edu.educationLevel === "school" ? `${edu.standard} - ${edu.schoolName}` : `${edu.degreeName} - ${edu.collegeName}`}
                </Typography>
                <Typography variant="body2" color={primaryColor} sx={textStyle}>
                  {edu.educationLevel === "school" ? edu.schoolLocation : edu.collegeLocation}
                </Typography>
                {edu.educationLevel === "school" && edu.stream && (
                  <Typography variant="body2" color={primaryColor} sx={textStyle}>Stream: {edu.stream}</Typography>
                )}
                {edu.educationLevel === "college" && edu.specialization && (
                  <Typography variant="body2" color={primaryColor} sx={textStyle}>Specialization: {edu.specialization}</Typography>
                )}
                {edu.gradMonth && edu.gradYear && (
                  <Typography variant="body2" color={primaryColor} sx={textStyle}>Graduated: {edu.gradMonth} {edu.gradYear}</Typography>
                )}
                {edu.percentage && (
                  <Typography variant="body2" color={primaryColor} sx={textStyle}>Percentage: {edu.percentage}%</Typography>
                )}
                {edu.cgpa && (
                  <Typography variant="body2" color={primaryColor} sx={textStyle}>CGPA: {edu.cgpa}</Typography>
                )}
              </Box>
            ))}
          </Box>
        ) : null;

      case 'skills':
        return (savedSkills.length > 0 || softwareSkills.length > 0) ? (
          <Box mb={`${sectionSpacing}px`} className="resume-section" id="skills-section" sx={{ fontFamily: font, ...textStyle }}>
            <SectionHeading>Skills</SectionHeading>
            <Box component="ul" sx={{ pl: 2, mt: 0, fontFamily: font, ...textStyle }}>
              {savedSkills.map((skill, i) => (
                <Typography key={i} component="li" variant="body1" color={primaryColor} sx={textStyle}>
                  {skill.name} {skill.rating ? `(${skill.rating}%)` : ''}
                </Typography>
              ))}
              {softwareSkills.map((skill, i) => (
                <Typography key={`software-${i}`} component="li" variant="body1" color={primaryColor} sx={textStyle}>
                  {skill.name} {skill.level ? `- ${skill.level}` : ''}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'languages':
        return savedLanguages.length > 0 ? (
          <Box mb={`${sectionSpacing}px`} className="resume-section" id="languages-section" sx={{ fontFamily: font, ...textStyle }}>
            <SectionHeading>Languages</SectionHeading>
            <Box component="ul" sx={{ pl: 2, mt: 0, fontFamily: font, ...textStyle }}>
              {savedLanguages.map((lang, index) => (
                <Typography key={index} component="li" variant="body1" color={primaryColor} sx={textStyle}>
                  {lang.name} {lang.level ? `– ${lang.level}` : ""}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'certifications':
        return savedCertifications.length > 0 ? (
          <Box mb={`${sectionSpacing}px`} className="resume-section" id="certifications-section" sx={{ fontFamily: font, ...textStyle }}>
            <SectionHeading>Certifications</SectionHeading>
            <Box component="ul" sx={{ pl: 2, mt: 0, fontFamily: font, ...textStyle }}>
              {savedCertifications.map((cert, index) => (
                <Typography key={index} component="li" variant="body1" color={primaryColor} sx={textStyle}>
                  {cert.name} – {cert.provider} ({cert.year})
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'accomplishments':
        return savedAccomplishments.length > 0 ? (
          <Box mb={`${sectionSpacing}px`} className="resume-section" id="accomplishments-section" sx={{ fontFamily: font, ...textStyle }}>
            <SectionHeading>Accomplishments</SectionHeading>
            <Box component="ul" sx={{ pl: 2, mt: 0, fontFamily: font, ...textStyle }}>
              {savedAccomplishments.map((accomplishment, index) => (
                <Typography key={index} component="li" variant="body1" color={primaryColor} sx={textStyle}>
                  {accomplishment}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'additionalInfo':
        return savedAdditionalInfo.length > 0 ? (
          <Box mb={`${sectionSpacing}px`} className="resume-section" id="additional-info-section" sx={{ fontFamily: font, ...textStyle }}>
            <SectionHeading>Additional Information</SectionHeading>
            <Box component="ul" sx={{ pl: 2, mt: 0, fontFamily: font, ...textStyle }}>
              {savedAdditionalInfo.map((info, index) => (
                <Typography key={index} component="li" variant="body1" color={primaryColor} sx={textStyle}>
                  {info}
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'volunteering':
        return savedVolunteering.length > 0 ? (
          <Box mb={`${sectionSpacing}px`} className="resume-section" id="volunteering-section" sx={{ fontFamily: font, ...textStyle }}>
            <SectionHeading>Volunteering</SectionHeading>
            {savedVolunteering.map((vol, index) => (
              <Box key={index} sx={{ mb: `${paragraphSpacing}px`, fontFamily: font }}>
                <Typography color={primaryColor} sx={{ ...textStyle, fontWeight: "bold" }}>{vol.subtopic}</Typography>
                <Typography color={primaryColor} sx={textStyle}>{vol.fromDate} to {vol.toDate}</Typography>
                <Typography color={primaryColor} sx={{ ...textStyle, whiteSpace: "pre-line", mt: 0.5 }}>{vol.content}</Typography>
              </Box>
            ))}
          </Box>
        ) : null;

      case 'interests':
        return savedInterests.length > 0 ? (
          <Box mb={`${sectionSpacing}px`} className="resume-section" id="interests-section" sx={{ fontFamily: font, ...textStyle }}>
            <SectionHeading>Interests</SectionHeading>
            <Box component="ul" sx={{ pl: 2, mt: 0, fontFamily: font, ...textStyle }}>
              {savedInterests.map((interest, index) => (
                <Typography key={index} component="li" variant="body1" color={primaryColor} sx={textStyle}>{interest}</Typography>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'websites':
        return savedWebsites.length > 0 ? (
          <Box mb={`${sectionSpacing}px`} className="resume-section" id="websites-section" sx={{ fontFamily: font, ...textStyle }}>
            <SectionHeading>Websites &amp; Portfolios</SectionHeading>
            <Box component="ul" sx={{ pl: 2, mt: 0, fontFamily: font, ...textStyle }}>
              {savedWebsites.map((website, index) => (
                <Typography key={index} component="li" variant="body1" color={primaryColor} sx={textStyle}>
                  <a
                    href={website.url.startsWith('http') ? website.url : `https://${website.url}`}
                    target="_blank" rel="noopener noreferrer"
                    style={{ textDecoration: 'none', color: primaryColor, fontFamily: font, fontSize: fontSize, fontStyle: fontStyle }}
                  >
                    {website.name || website.url}
                  </a>
                </Typography>
              ))}
            </Box>
          </Box>
        ) : null;

      case 'references':
        return savedReferences.length > 0 ? (
          <Box mb={`${sectionSpacing}px`} className="resume-section" id="references-section" sx={{ fontFamily: font, ...textStyle }}>
            <SectionHeading>References</SectionHeading>
            {savedReferences.map((ref, index) => (
              <Typography key={index} color={primaryColor} sx={{ ...textStyle, mb: `${paragraphSpacing}px` }} whiteSpace="pre-line">
                {ref}
              </Typography>
            ))}
          </Box>
        ) : null;

      default:
        if (sectionId && sectionId.startsWith('custom_')) {
          const customSectionName = sectionId.replace('custom_', '');
          const items = customSections[customSectionName] || [];
          if (items && items.length > 0) {
            return (
              <Box mb={`${sectionSpacing}px`} className="resume-section" id={`custom-${customSectionName}-section`} sx={{ fontFamily: font, ...textStyle }}>
                <SectionHeading>{customSectionName.toUpperCase()}</SectionHeading>
                {items.map((item, i) => (
                  <Typography key={i} sx={{ mb: `${paragraphSpacing / 2}px`, fontSize: fontSize, fontFamily: font, color: primaryColor, lineHeight: `${lineSpacing}px` }} whiteSpace="pre-line">
                    • {item}
                  </Typography>
                ))}
              </Box>
            );
          }
          return null;
        }
        return null;
    }
  };

  const saveDownloadHistory = async (format, fileName, fileSize) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;
      await fetch("https://resume-maker-lc.onrender.com/api/resumes/save-download", {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ templateId: 12, fileName, fileFormat: format, fileSize, themeColor: primaryColor, downloadedAt: new Date().toISOString() }),
      });
    } catch (error) {
      console.error("Error saving download history:", error);
    }
  };

  const handlePDFDownload = async () => {
    const resumeElement = document.getElementById('resume-container');
    if (!resumeElement) return;
    try {
      const resumePages = resumeElement.querySelectorAll('.resume-page');
      if (resumePages.length === 0) return;
      const pdf = new jsPDF("p", "mm", pageSize.toLowerCase());
      const pageWidth = pdf.internal.pageSize.getWidth();
      for (let i = 0; i < resumePages.length; i++) {
        if (i > 0) pdf.addPage();
        const canvas = await html2canvas(resumePages[i], { scale: 2, useCORS: true, allowTaint: true, backgroundColor: "#ffffff", logging: false });
        const imgData = canvas.toDataURL("image/png");
        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight, undefined, 'FAST');
      }
      const pdfBlob = pdf.output('blob');
      await saveDownloadHistory("pdf", "resume", pdfBlob.size);
      pdf.save("resume.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Error generating PDF. Please try again.");
    }
  };

  const handlePNGDownload = async () => {
    const resumeElement = document.getElementById('resume-container');
    if (!resumeElement) return;
    try {
      const resumePages = resumeElement.querySelectorAll('.resume-page');
      if (resumePages.length === 0) return;
      for (let i = 0; i < resumePages.length; i++) {
        const canvas = await html2canvas(resumePages[i], { scale: 2, useCORS: true, allowTaint: true, backgroundColor: "#ffffff" });
        const link = document.createElement('a');
        link.download = resumePages.length === 1 ? `resume.png` : `resume-page-${i + 1}.png`;
        link.href = canvas.toDataURL();
        if (i === 0) { link.click(); } else { setTimeout(() => link.click(), i * 500); }
      }
      await saveDownloadHistory("png", "resume", 0);
      if (resumePages.length > 1) alert(`Downloading ${resumePages.length} PNG files.`);
    } catch (error) {
      console.error("Error generating PNG:", error);
      alert("Error generating PNG files. Please try again.");
    }
  };

  const handleDownload = (format) => {
    if (format === 'pdf') handlePDFDownload();
    else if (format === 'png') handlePNGDownload();
  };

  // ─── FIX: addFontToHtmlContent ────────────────────────────────────────────────
  // Previously h1-h6 replacements were overriding MUI's inline headingSize with
  // the body fontSize. Now we SKIP h1-h6 entirely — their inline styles from
  // the SectionHeading <div> already carry the correct headingSize.
  const addFontToHtmlContent = (htmlContent) => {
    if (!htmlContent) return '';
    return htmlContent
      .replace(/<div(?![^>]*style=)/g, `<div style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px;"`)
      .replace(/<p(?![^>]*style=)/g, `<p style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px;"`)
      .replace(/<span(?![^>]*style=)/g, `<span style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px;"`)
      // ↓ h1-h6 intentionally OMITTED — SectionHeading already has correct inline styles
      .replace(/<li(?![^>]*style=)/g, `<li style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px;"`)
      .replace(/<ul(?![^>]*style=)/g, `<ul style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px;"`)
      .replace(/<ol(?![^>]*style=)/g, `<ol style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle}; line-height: ${lineSpacing}px;"`)
      .replace(/<a(?![^>]*style=)/g, `<a style="font-family: ${font}; font-size: ${fontSize}; font-style: ${fontStyle};"`)
  };

  const splitContentIntoPages = useCallback(() => {
    if (!resumeRef.current) return;
    const sections = Array.from(resumeRef.current.querySelectorAll('.resume-section'));
    const pageHeightInPx = parseFloat(page.height.replace('mm', '')) * 3.78;
    const availableHeight = pageHeightInPx - (2 * topBottomMargin) - 2 * 16;

    let newPages = [], currentPageSections = [], currentHeight = 0;
    sections.forEach(section => {
      const sectionHeight = section.offsetHeight;
      if (currentHeight + sectionHeight > availableHeight) {
        if (currentPageSections.length > 0) newPages.push(currentPageSections);
        currentPageSections = [section.outerHTML];
        currentHeight = sectionHeight;
      } else {
        currentPageSections.push(section.outerHTML);
        currentHeight += sectionHeight;
      }
    });
    if (currentPageSections.length > 0) newPages.push(currentPageSections);
    setPages(newPages);
  }, [page.height, topBottomMargin, workExperiences, educationEntries, savedSkills, savedSummary, savedAdditionalInfo, savedLanguages, savedAccomplishments, savedCertifications, savedReferences, softwareSkills, savedVolunteering, savedInterests, savedWebsites, customSections, sectionOrder, sectionSpacing, paragraphSpacing, lineSpacing, fontSize, headingSize, font, primaryColor]);
  
  useEffect(() => {
    const timeoutId = setTimeout(() => splitContentIntoPages(), 500);
    return () => clearTimeout(timeoutId);
  }, [splitContentIntoPages]);

  const renderContentForMeasurement = () => (
    <Box ref={resumeRef} sx={{ visibility: 'hidden', position: 'absolute', fontFamily: font }}>
      <Box sx={{ p: `0px ${sideMargins}px`, fontFamily: font, color: primaryColor }}>
        <Box className="resume-section" sx={{ textAlign: 'center', mb: `${sectionSpacing}px`, fontFamily: font, ...textStyle }}>
          <Typography variant="h3" component="h1" fontWeight="bold" sx={{ ...textStyle, fontSize: `calc(${headingSize} * 1.8)` }}>
            {data.firstName} {data.lastName}
          </Typography>
          <Typography variant="h6" sx={{ ...textStyle, letterSpacing: 2, fontSize: `calc(${fontSize} * 1.3)` }}>
            {data.currentPosition}
          </Typography>
        </Box>

        <Divider sx={{ my: `${sectionSpacing}px`, borderWidth: `${lineWeight}px`, borderColor: primaryColor }} />

        <Box className="resume-section" sx={{ mb: `${sectionSpacing}px`, display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 3, fontFamily: font, ...textStyle }}>
          {data.email && (
            <Box display="flex" alignItems="center">
              <EmailIcon sx={{ mr: 1, color: primaryColor }} />
              <Typography variant="body1" color={primaryColor} sx={textStyle}>{data.email}</Typography>
            </Box>
          )}
          {data.phone && (
            <Box display="flex" alignItems="center">
              <PhoneIcon sx={{ mr: 1, color: primaryColor }} />
              <Typography variant="body1" color={primaryColor} sx={textStyle}>{data.phone}</Typography>
            </Box>
          )}
          {data.city && (
            <Box display="flex" alignItems="center">
              <LocationOnIcon sx={{ mr: 1, color: primaryColor }} />
              <Typography variant="body1" color={primaryColor} sx={textStyle}>{data.city}</Typography>
            </Box>
          )}
          {data.website && (
            <Box display="flex" alignItems="center">
              <LanguageIcon sx={{ mr: 1, color: primaryColor }} />
              <a href={data.website.startsWith('http') ? data.website : `https://${data.website}`} target="_blank" rel="noopener noreferrer"
                style={{ color: primaryColor, textDecoration: 'none', fontFamily: font, fontSize: fontSize, fontStyle: fontStyle }}>
                {data.website.replace(/^https?:\/\/(www\.)?/, '')}
              </a>
            </Box>
          )}
          {data.linkedIn && (
            <Box display="flex" alignItems="center">
              <LinkedInIcon sx={{ mr: 1, color: '#0077b5' }} />
              <a href={data.linkedIn.startsWith('http') ? data.linkedIn : `https://${data.linkedIn}`} target="_blank" rel="noopener noreferrer"
                style={{ color: primaryColor, textDecoration: 'none', fontFamily: font, fontSize: fontSize, fontStyle: fontStyle }}>
                {data.linkedIn.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\/|linkedin\.com\/in\//, '')}
              </a>
            </Box>
          )}
          {data.drivingLicence && (
            <Box display="flex" alignItems="center">
              <DriveEtaIcon sx={{ mr: 1, color: '#4caf50' }} />
              <Typography variant="body1" color={primaryColor} sx={textStyle}>DL: {data.drivingLicence}</Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: `${sectionSpacing}px`, borderWidth: `${lineWeight}px`, borderColor: primaryColor }} />

        {sectionOrder.map(sectionId => (
          <React.Fragment key={sectionId}>{renderSectionContent(sectionId)}</React.Fragment>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", position: "relative", fontFamily: font, color: primaryColor }}>
      {!exporting && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, gap: 2, '@media print': { display: 'none' } }}>
          <IconButton onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))} disabled={currentPage === 0} color="primary"><ChevronLeft /></IconButton>
          <Typography sx={{ mx: 2 }}>Page {currentPage + 1} of {Math.max(1, pages.length)}</Typography>
          <IconButton onClick={() => setCurrentPage(prev => Math.min(pages.length - 1, prev + 1))} disabled={currentPage === Math.max(0, pages.length - 1)} color="primary"><ChevronRight /></IconButton>
          {enableDragDrop && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', ml: 2 }}>
              <Button onClick={handleArrangeSectionsClick} variant="contained" startIcon={<Reorder />} color="primary">Arrange Sections</Button>
              <Button onClick={resetSectionOrder} variant="outlined" color="secondary" size="small">Reset Section Order</Button>
            </Box>
          )}
        </Box>
      )}

      {renderContentForMeasurement()}

      <Box id="resume-container">
        {pages.length > 0 ? (
          pages.map((pageContent, index) => (
            <Box
              key={index}
              className="resume-page"
              sx={{
                width: page.width, minHeight: page.height, mx: "auto",
                boxShadow: index === currentPage ? 3 : 0,
                bgcolor: "#fff", color: primaryColor, fontFamily: font,
                fontSize, fontStyle, lineHeight: `${lineSpacing}px`,
                p: `${topBottomMargin}px ${sideMargins}px`,
                display: exporting ? 'block' : (index === currentPage ? 'block' : 'none'),
                pageBreakAfter: "always",
                "@media print": { display: 'block', width: page.width, height: "auto", minHeight: page.height, boxShadow: "none", m: 0 },
              }}
            >
              <div style={textStyle} dangerouslySetInnerHTML={{ __html: addFontToHtmlContent(pageContent.join('')) }} />
            </Box>
          ))
        ) : (
          <Box className="resume-page" sx={{ width: page.width, minHeight: page.height, mx: "auto", boxShadow: 3, bgcolor: "#fff", fontFamily: font, fontSize, fontStyle, lineHeight: `${lineSpacing}px`, p: `${topBottomMargin}px ${sideMargins}px`, display: "flex", flexDirection: "column" }}>
            <Typography sx={{ p: 2, ...textStyle }}>Loading resume...</Typography>
          </Box>
        )}
      </Box>

      <Dialog open={arrangeDialogOpen} onClose={handleArrangeDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center"><Reorder sx={{ mr: 1 }} />Arrange Resume Sections</Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            Drag and drop to reorder sections.
          </Typography>
          {renderDragPanel()}
        </DialogContent>
        <DialogActions>
          <Button onClick={resetSectionOrder} color="secondary">Reset Order</Button>
          <Button onClick={handleArrangeDialogClose} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const resumeMeta = { hasPhoto: false, columns: 1 };
export default Resume12;